import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, opts = {}) => {
    const { data } = await api.post("/auth/login", {
      email,
      password,
      turnstile_token: opts.turnstile_token || "",
    });
    // Admin 2FA flow: don't set user, let caller handle
    if (data.requires_2fa || data.requires_2fa_setup) {
      return data;
    }
    setUser(data);
    return data;
  };

  const register = async (email, password, name, tenantName, claimCode, opts = {}) => {
    const payload = { email, password, name, tenant_name: tenantName };
    if (claimCode) payload.claim_code = claimCode;
    if (opts.turnstile_token) payload.turnstile_token = opts.turnstile_token;
    if (opts.honeypot !== undefined) payload.honeypot = opts.honeypot;
    if (opts.submit_duration_ms !== undefined) payload.submit_duration_ms = opts.submit_duration_ms;
    if (opts.referral_code) payload.referral_code = opts.referral_code;
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
